import { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { slidingSessionConfig } from './api/config';
import { authService } from './api/dbService';
import LoadingSpinner from './components/LoadingSpinner';

import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import ShipmentView from './pages/ShipmentView';
import ShipmentAdd from './pages/ShipmentAdd';
import ShipmentHistory from './pages/ShipmentHistory';
import Shipments from './pages/Shipments';
import InactiveDevices from './pages/InactiveDevices';
import Devices from './pages/Devices';
import UserManagement from './pages/UserManagement';
import Viewers from './pages/Viewers';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import NotFound from './pages/NotFound';

function ProtectedRoute({ children, isAuthenticated, isLoading }) {
  if (isLoading) return <LoadingSpinner overlay />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function ViewerBlock({ children }) {
  const userData = JSON.parse(localStorage.getItem('current_user') || '{}');
  const role = userData?.role || userData?.rol || 'user';
  if (role === 'viewer') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(slidingSessionConfig.tokenKey);
    if (token) {
      authService.validateToken()
        .then(() => setIsAuthenticated(true))
        .catch(() => {
          localStorage.removeItem(slidingSessionConfig.tokenKey);
          localStorage.removeItem(slidingSessionConfig.userKey);
          localStorage.removeItem('user');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }

    const handleAuthChanged = () => setIsAuthenticated(true);
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      localStorage.removeItem(slidingSessionConfig.tokenKey);
      localStorage.removeItem(slidingSessionConfig.userKey);
      localStorage.removeItem('user');
    };

    window.addEventListener('auth-state-changed', handleAuthChanged);
    window.addEventListener('auth-expired', handleAuthExpired);
    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChanged);
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  const p = { isAuthenticated, isLoading };

  return (
    <HashRouter>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="light" limit={5} />
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login setIsAuthenticated={setIsAuthenticated} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/" element={<ProtectedRoute {...p}><Home /></ProtectedRoute>} />
        <Route path="/view/:id" element={<ProtectedRoute {...p}><ShipmentView /></ProtectedRoute>} />
        <Route path="/add" element={<ProtectedRoute {...p}><ViewerBlock><ShipmentAdd /></ViewerBlock></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute {...p}><ShipmentHistory /></ProtectedRoute>} />
        <Route path="/shipments" element={<ProtectedRoute {...p}><Shipments /></ProtectedRoute>} />
        <Route path="/inactive-devices" element={<ProtectedRoute {...p}><ViewerBlock><InactiveDevices /></ViewerBlock></ProtectedRoute>} />
        <Route path="/devices" element={<ProtectedRoute {...p}><ViewerBlock><Devices /></ViewerBlock></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute {...p}><UserManagement /></ProtectedRoute>} />
        <Route path="/viewers" element={<ProtectedRoute {...p}><ViewerBlock><Viewers /></ViewerBlock></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute {...p}><Profile /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute {...p}><Notifications /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </HashRouter>
  );
}
